import { useEffect } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useT } from "../i18n";
import { useSitzung } from "./Sitzung";
import { Zeichen } from "../bausteine/Zeichen";
import { useTelefon } from "./geraet";
import { aktivesFach, anlegenFuer, blattFach, leisteFuer, type Handyweg } from "./handywege";
import { oberstesSchliessen, useBlattOffen } from "./schliessanfrage";
import { nachschlagenOeffnen, useNachschlagenOffen } from "./nachschlagen";
import { anlegenOeffnen, useAnlegenOffen, type Anlegegruppe } from "./anlegen";
import { useAnlegegruppen } from "./Anlegeblatt";
import "../stile/handy-leiste.css";

/* ══════════════════════════════════════════════════════════════════
   Die Leiste am unteren Rand — die Gliederung am Telefon.

   Luca, 17.09.2026: *„Vor allem muss ich als Verkäufer ein einfaches
   Menü haben, eine Bottom-Navbar … Das muss vom UI-Konzept auch schon
   besser aussehen, und dann mach das direkt auch in die Bottom-Navbar
   rein."*

   **Sie steht in der Schale, nicht in der Maske.** Sonst müsste jede
   der zwölf Handy-Masken sie selbst einhängen, und die dreizehnte
   vergäße es. Sie liegt im `#maskenraum` und damit **unter** dem Blatt
   von unten und dem Deckblatt: Wer ein Angebot schreibt, soll nicht
   mitten im Blatt auf einen Navigationspunkt treffen.

   **Welche Fächer, steht in `handywege.ts`** — derselben Datei, aus der
   auch die Liste „Mehr" liest. Zwei Listen für dieselbe Frage lägen
   beim nächsten Punkt auseinander.

   **Der aktive Punkt trägt Farbe UND Marke** (28-designkonzept.md:
   Farbe ist nie der einzige Träger): den Streifen in Hausgelb an der
   Oberkante, die Schrift in abgedunkeltem Gelb, dazu `aria-current`.
   Auf dem Streifen steht keine Schrift — er ist Fläche, kein Text, und
   darf deshalb das helle Hausgelb tragen.

   **Das Polster unten gehört ihr selbst.** `env(safe-area-inset-bottom)`
   sitzt in ihrem eigenen Innenabstand: Auf einem Gerät mit Balken steht
   sie darüber, auf einem ohne wird sie nicht künstlich hoch.
   ══════════════════════════════════════════════════════════════════ */

const KEINE: Handyweg[] = [];

/**
 * Die Fächer, die gerade gelten — oder eine leere Liste.
 *
 * Die Schale fragt damit, ob sie unten Platz lassen muss; die Leiste
 * selbst fragt, was sie zeichnet. **Eine Frage, eine Antwort.**
 *
 * **Das Gerät entscheidet, nicht die Adresse** (17.09.2026). Bis dahin
 * stand die Leiste nur unter `/handy…` — wer am Telefon auf `/kunden`
 * oder `/auftraege` stand, hatte keine und kam nur über den Menüknopf
 * weiter. Luca: *„Wir wollen die Mobile-App ja nicht an die Adresse
 * binden, sondern daran, dass wir von dem Mobile aus arbeiten."* Jetzt
 * steht sie am Telefon über **jeder** Maske und am Schreibtisch über
 * keiner, auch nicht über `/handy` — dort ist die Seitenleiste die
 * Navigation.
 */
export function useHandyleiste(): Handyweg[] {
  const { ich } = useSitzung();
  const telefon = useTelefon();
  if (!telefon) return KEINE;
  return leisteFuer(ich?.rollen ?? []);
}

/* ══════════════ Auch über dem Einsatz steht sie ══════════════

   Luca, 18.09.2026, nach dem Durchklicken des Einsatzes am Telefon:

   > *„Wenn ich einen Vorgang öffne, dann möchte ich gerne, dass die
   > Bottom-Nav-Bar trotzdem sichtbar bleibt und das ‚Vorbereiten' und ‚Foto'
   > dann entsprechend über der Bottom-Nav-Bar ist. Ansonsten ist das
   > irgendwie zu unruhig."*

   **Bis hierher gab die Schale ihre Leiste auf `/handy/einsatz…` ab** — mit
   der Überlegung, zwei Balken übereinander kosteten 110 px und der Einsatz
   sei eine Aufgabe und kein Navigationsziel. Am Gerät stimmte die Rechnung
   und das Ergebnis nicht: Wer aus der Tagesmappe einen Einsatz öffnet,
   verliert den Boden, auf dem er eben noch stand, und bekommt an derselben
   Stelle einen anderen Balken. Das ist kein gewonnener Platz, das ist ein
   Sprung.

   **Jetzt bleibt sie stehen, und die Aktionsleiste legt sich darüber.** Der
   Einsatzschirm rechnet dafür mit `--ez-unten` (`stile/einsatz.css`): die
   Höhe der Leiste (`--hleiste-hoehe` in `stile/schale.css`, quer 44 px) plus
   `env(safe-area-inset-bottom)`, solange `[data-hleiste="ja"]` am Maskenraum
   steht. Keine zweite Zahl, nirgends. */

/* ══════════════ Kein zweiter Kreis mehr (19.09.2026) ══════════════

   Am 18.09.2026 trat der Kreis der Leiste zurück, wo eine Maske ihren
   eigenen, reicheren Anlegeknopf mitbrachte — in der Kundenakte stand ein
   Fächer mit „Notiz", „Vermieten", „Angebot" und „Preis nachschlagen".
   Luca am 19.09.2026: *„Der Plus-Button kann da ja auch weg. Wir haben
   dann Plus unten in der Bottom-Nav-Bar."*

   **Seitdem gibt es einen Kreis, und die Maske gibt ihm ihre Griffe**
   (`schale/anlegen.ts`, `useAnlegegriffe`). Er öffnet das Blatt
   (`Anlegeblatt.tsx`): oben, was die Maske anbietet, darunter, was immer
   geht. Zwei Bedienweisen an derselben Stelle — einer springt, einer
   fächert — gibt es nicht mehr. */

/* ══════════════ Der Anlegeknopf in der Mitte (18.09.2026) ══════════════

   Nach dem Vorbild von OVIS (42-vorbild-ovis.md). Drei Dinge daran sind
   abgeschrieben, weil sie dort begründet stehen:

   1. **Die Mitte ist ein Platzhalter, nicht der Knopf.** Die Leiste hält
      64 px frei; der Kreis schwebt darüber und ragt nach oben hinaus. Wäre er
      ein Flex-Kind, müsste die Leiste so hoch werden wie er.
      — *Überholt am 19.09.2026: Die Mitte ist jetzt der Knopf, und der Kreis
      schwebt darin (siehe „Die Mitte IST der Knopf" unten). Der Grund gegen
      das Flex-Kind bleibt richtig — er gilt jetzt für den Kreis im Knopf.*
   2. **Ohne Anlegerecht fällt der Platzhalter auf null zusammen**
      (`handy-leiste.css`) — eine Leiste mit einem Loch liest sich als kaputt.
      — *Seit dem 19.09.2026 wird er dann gar nicht erst gezeichnet.*
   3. **Die Fächer teilen sich links und rechts davon auf**, und zwar zur
      größeren Hälfte nach links: Bei drei Fächern und „Mehr" sind das zwei
      und zwei, bei „Heute · Mehr" eines und eines.

   **Er trägt Hausgelb als Fläche und `--auf-aktion` als Zeichen** — nie weiß
   (28-designkonzept.md, „Aktion ist Fläche, Zustand ist Punkt"). Das Pluszeichen
   ist hier gezeichnet und nicht in `bausteine/Zeichen.tsx`: Dort steht, was
   Masken brauchen; dies ist ein Teil der Schale.

   **Seit dem 19.09.2026 ein Knopf und kein Verweis.** Er öffnet das Blatt
   (`Anlegeblatt.tsx`) — außer es stünde darin genau ein Griff: Dann tut er
   gleich, was die Zeile täte (Regel 11). Offen trägt er `aria-expanded`;
   der Ring für „hier bist du" bleibt dem Fall, in dem es einen einzigen
   Weg gibt und man auf ihm steht. */

/* ══════════════ Die Mitte IST der Knopf (19.09.2026, abends) ══════════════

   Luca: *„Das ist auch nicht immer zuverlässig in der Bottom-Navbar. Das musst
   du auf jeden Fall komplett überarbeiten, damit das sauber ist, immer
   zuverlässig funktioniert und das Touch-Gefühl wirklich gut ist."*

   **Gemessen auf 390 × 844 mit dem Balken des iPhones (34 px):** Von der
   gezeichneten Leiste trafen **41,6 %** nichts — 2.487 von 5.980 Punkten eines
   Rasters (3 × 2 px) landeten auf der Leiste selbst oder auf dem Platzhalter
   und nicht auf einem Knopf. Zwei Löcher:

   1. **Der Streifen über dem Balken** (die unteren 34 px, siehe
      `handy-leiste.css`) gehörte keinem Fach.
   2. **Die Ecken um den Kreis.** Der Platzhalter war 64 px breit und
      `aria-hidden`, der Kreis 60 px rund und schwebte als eigenes Kind der
      Leiste darüber. Wer neben den Kreis oder darunter traf — und der Daumen
      trifft von unten —, traf den Platzhalter: kein Knopf, kein Klick.

   **Jetzt ist die Mitte selbst der Knopf** — die ganze Spalte von der
   Oberkante der Leiste bis an den Rand des Schirms, und der gelbe Kreis ist
   ein Kind darin (`hleiste-anlegen`), das weiter um 10 px nach oben
   hinausragt. Auch was darüber hinausragt, gehört damit zum Knopf. Die
   Klassen sind geblieben, damit `telefon.css` (quer: 48-px-Kreis, 56-px-Spalte)
   unverändert greift: `hleiste-mitte` ist die Spalte, `hleiste-anlegen` der
   Kreis.

   **Nebenbei steht der Kreis jetzt immer über seiner Spalte.** Bisher hing er
   mit `left: 50%` an der Leiste — bei einer ungeraden Zahl von Fächern
   (zwei links, eines rechts) wäre die Spalte aus der Mitte gerückt und der
   Kreis über dem Fach daneben gestanden. */
/* ══════════════ Plus oder Blitz (20.09.2026) ══════════════

   Luca: *„Wenn ich in einem Angebot bin oder einen Auftrag bilde, würde ich
   den Plus-Button anders machen, damit man sieht, dass das hier jetzt ein
   Action-Button ist. … Nicht Plus, sondern ein Blitz. … Diesen Action-Button
   machst du einfach immer dann, wenn es Optionen gibt, die wir hier wählen
   können. … Wenn das vom Plus zu einem Blitz wird, muss es auch ein bisschen
   animiert sein."*

   **Derselbe Knopf, zwei Zeichen:**

   | Zeichen | wann | was dahinter steht |
   |---|---|---|
   | **Plus** | nichts angemeldet | „Neu anlegen": Vermieten, Angebot, Kunde |
   | **Blitz** | die Maske hat Griffe angemeldet (`schale/anlegen.ts`) | „Aktionen": was an **dieser** Sache dran ist — „Angebot senden", „Übernehmen", „Position nachtragen" |

   **Warum nicht zwei Knöpfe:** Es ist dieselbe Stelle, dieselbe Fläche und
   dieselbe Geste; nur der Vorrat dahinter wechselt. Ein zweiter Knopf wäre
   eine zweite gelbe Fläche je Maske (28-designkonzept.md).

   **Beide Zeichen liegen übereinander im Kreis**, und der Wechsel gleitet in
   180 ms (`handy-leiste.css`): Wer eine Akte öffnet, sieht den Knopf
   umschlagen und weiß, dass er jetzt etwas anderes tut. Mit
   `prefers-reduced-motion` schlägt er ohne Bewegung um.

   **Gezeichnet, nicht aus `bausteine/Zeichen.tsx`:** Dort steht, was Masken
   brauchen; der Kreis ist Schale (siehe oben). */
/* ══════════════ Über einem Blatt wird er zum Kreuz (Signal, Paket 5) ══════════════

   Nach app-design-muster (`fassungen/telefon/mittiger-knopf.md`, „Kreuz — über
   jedem Blatt", und `fallen.md` Nr. 12 „Zwei Kreuze"): Liegt ein Blatt offen,
   steht der Kreis **über** ihm, das Plus dreht sich um 45° zum Kreuz, und ein
   Tipp schließt das oberste Blatt — mit derselben Anfrage wie Griff, `Esc` und
   Zurückgeste (`schliessanfrage.ts`), also auch mit „Verwerfen?". Das Kreuz im
   Blattkopf fällt dafür am Telefon weg (`stile/signal-telefon.css`), und das
   Blatt hält unten den Platz frei, auf dem der Kreis steht. */
function Anlegen({ gruppen, eigen, aktiv }: {
  gruppen: Anlegegruppe[]; eigen: boolean; aktiv: boolean;
}) {
  const t = useT();
  const offen = useAnlegenOffen();
  const blatt = useBlattOffen();
  const griffe = gruppen.flatMap((g) => g.griffe);
  const einer = griffe.length === 1 ? griffe[0]! : null;
  if (blatt) {
    return (
      <button
        type="button"
        className="hleiste-mitte"
        data-anlegen="ja"
        aria-label={t("allgemein.schliessen")}
        title={t("allgemein.schliessen")}
        onClick={oberstesSchliessen}
      >
        <span className="hleiste-anlegen" data-art="kreuz" aria-hidden="true">
          <svg className="hleiste-plus" viewBox="0 0 24 24" focusable="false">
            <path d="M12 5.5v13M5.5 12h13" />
          </svg>
          <svg className="hleiste-blitz" viewBox="0 0 24 24" focusable="false">
            <path d="M13 2 3.5 14h8.5l-1 8 9.5-12h-8.5l1-8Z" />
          </svg>
        </span>
      </button>
    );
  }
  const wort = einer
    ? t(einer.wort, einer.werte)
    : t(eigen ? "anlegen.schildMaske" : "anlegen.schild");
  return (
    <button
      type="button"
      className="hleiste-mitte"
      data-anlegen="ja"
      aria-label={wort}
      title={wort}
      {...(einer ? {} : { "aria-haspopup": "dialog" as const, "aria-expanded": offen })}
      {...(aktiv && einer ? { "aria-current": "page" as const } : {})}
      onClick={() => (einer ? einer.tun() : anlegenOeffnen())}
    >
      {/* **Kein Wort unter dem Kreis** (20.09.2026). Einen Tag lang stand dort,
          was der Blitz tut — „Übernehmen", „Vorbereitung starten". Luca: *„Da
          kommt darunter der Text ‚Übernehmen'. Der soll einfach nur der Button
          sein."* Er hat recht: Der Kreis ist kein Fach der Leiste, sondern
          eine Handlung, und die vier Wörter daneben sind eine Reihe, in die
          ein fünftes nicht gehört. Gesagt wird es trotzdem — in `aria-label`
          und `title`, also für Vorleser und für den langen Druck. */}
      <span className="hleiste-anlegen" data-art={eigen ? "blitz" : "plus"} aria-hidden="true">
        <svg className="hleiste-plus" viewBox="0 0 24 24" focusable="false">
          <path d="M12 5.5v13M5.5 12h13" />
        </svg>
        {/* Der Blitz: dieselbe Strichstärke wie das Plus, geschlossener Zug,
            damit die Spitzen nicht ausfransen. */}
        <svg className="hleiste-blitz" viewBox="0 0 24 24" focusable="false">
          <path d="M13 2 3.5 14h8.5l-1 8 9.5-12h-8.5l1-8Z" />
        </svg>
      </span>
    </button>
  );
}

/* ══════════════ Jedes Fach behält seinen Stand (Signal, Paket 5) ══════════════

   Muster `navigation.md` § 2, „Erneuter Tipp": Wer aus „Mehr › Aufträge" kurz
   nach „Heute" wechselt und wieder auf „Mehr" tippt, landet bei den Aufträgen
   und nicht auf der Liste davor. Ein Tipp auf das Fach, in dem man steht,
   führt zu seiner Wurzel — und steht man dort schon, nach oben. Gemerkt wird
   der Pfad je Fach für diesen Reiter (kein Speicher: ein neuer Start beginnt
   an den Wurzeln). */
const standJeFach = new Map<string, string>();

export function Handyleiste({ punkte }: { punkte: Handyweg[] }) {
  const t = useT();
  const { ich } = useSitzung();
  const { location } = useRouterState();
  const nachschlagenOffen = useNachschlagenOffen();
  const blatt = useBlattOffen();
  const { gruppen, eigen } = useAnlegegruppen();
  const anlegenWegFuerStand = anlegenFuer(ich?.rollen ?? []);
  const aktivFuerStand = punkte.length
    ? aktivesFach(anlegenWegFuerStand ? [...punkte, anlegenWegFuerStand] : punkte, location.pathname)
    : null;
  useEffect(() => {
    if (aktivFuerStand) standJeFach.set(aktivFuerStand, location.pathname);
  }, [aktivFuerStand, location.pathname]);
  if (!punkte.length) return null;
  /* **Der Weg der Rolle zählt für die Marke weiter mit.** Sonst leuchtete auf
     `/vermieten` das Fach „Mehr", weil der Weg zu keinem Fach gehört — eine
     Marke unter einem Platz, in dem man nicht ist. */
  const anlegenWeg = anlegenFuer(ich?.rollen ?? []);
  const anlegen = gruppen.some((g) => g.griffe.length > 0);
  const aktiv = aktivesFach(anlegenWeg ? [...punkte, anlegenWeg] : punkte, location.pathname);
  /* Ein Fach, das ein Blatt öffnet, ist ein Knopf und kein Verweis. Offen ist
     es `aria-expanded` — nicht `aria-current`: Ein Blatt ist keine Seite. */
  const offen = { mehr: false, nachschlagen: nachschlagenOffen };
  const oeffnen = { mehr: () => undefined, nachschlagen: nachschlagenOeffnen };
  const schnitt = Math.ceil(punkte.length / 2);
  /* Die Rollfläche der Maske: Jede Telefonmaske rollt in sich selbst
     (`.handy`, die Rollfläche der Vorgangslisten, das Blatt einer Akte) —
     gesucht wird die erste, die etwas zu rollen hat. */
  const nachObenRollen = () => {
    const raum = document.getElementById("maskenraum");
    const flaechen = raum ? [raum, ...raum.querySelectorAll<HTMLElement>("*")] : [];
    for (const el of flaechen) {
      if (el.scrollTop > 0) {
        el.scrollTo({ top: 0, behavior: "smooth" });
        /* Wo sanftes Rollen nicht greift — ein verborgener Reiter, ein
           Browser, der es abgeschaltet hat —, springt es nach einem
           Wimpernschlag hart nach oben. Oben ankommen ist wichtiger als
           der Weg dorthin. */
        window.setTimeout(() => { if (el.scrollTop > 0) el.scrollTop = 0; }, 280);
        return;
      }
    }
  };
  const fach = (p: Handyweg) => blattFach(p) ? (
        /* Das Blatt liegt über der Maske, die stehen bleibt — „Mehr" trägt die
           Wege der Rolle (`Mehrblatt.tsx`), „Preis" und „Nachschlagen" die
           Auskunft (`masken/NachschlagenTelefon.tsx`). „Mehr" leuchtet
           zusätzlich, wenn man über das Blatt auf einem seiner Wege steht. */
        <button
          key={p.weg}
          type="button"
          className="hleiste-punkt"
          aria-haspopup="dialog"
          aria-expanded={offen[blattFach(p) as "mehr" | "nachschlagen"]}
          onClick={oeffnen[blattFach(p) as "mehr" | "nachschlagen"]}
          {...(p.weg === aktiv ? { "aria-current": "page" as const } : {})}
        >
          <Zeichen name={p.zeichen} />
          <span className="hleiste-wort">{t(p.text)}</span>
        </button>
      ) : (
        <Link
          key={p.weg}
          /* Das eigene Fach führt zur Wurzel, ein anderes dorthin, wo man in
             ihm zuletzt stand (siehe „Jedes Fach behält seinen Stand"). */
          to={(p.weg === aktiv ? p.weg : standJeFach.get(p.weg) ?? p.weg) as never}
          className="hleiste-punkt"
          /* `exact`: Ohne das hält der Router `/heute` unter einem Blattweg
             für die aktuelle Seite und setzt selbst `aria-current` — dann
             leuchten zwei Fächer zugleich. Welches wirklich gilt, weiß
             `aktivesFach`, und nur das setzt die Marke. */
          activeOptions={{ exact: true }}
          /* **Kein Vorladen an der Leiste** (20.09.2026). Am echten
             iOS-WebKit gemessen: Ein Knopf antwortet 2–3 ms nach dem
             Loslassen, ein Verweis erst nach rund 58 ms — dazwischen liegt
             die Arbeit, die `defaultPreload: "intent"` beim Berühren
             anstößt. Die vier Fächer sind die meistgetippten Ziele der
             Anwendung und ohnehin geladen; anderswo bleibt das Vorladen. */
          preload={false}
          {...(p.weg === aktiv ? { "aria-current": "page" as const } : {})}
          /* **Der zweite Tipp auf das eigene Fach rollt nach oben** — die
             Gewohnheit jeder App am Telefon. Vorher passierte nichts, und
             das liest sich wie „hat nicht reagiert". */
          onClick={(e) => {
            if (p.weg !== aktiv) return;
            if (location.pathname === p.weg) { e.preventDefault(); nachObenRollen(); }
          }}
        >
          <Zeichen name={p.zeichen} />
          <span className="hleiste-wort">{t(p.text)}</span>
        </Link>
      );

  return (
    <nav className="hleiste" aria-label={t("handyLeiste.schild")} data-blatt={blatt ? "offen" : "zu"}>
      {punkte.slice(0, schnitt).map(fach)}
      {/* Die Mitte ist der Knopf (siehe `Anlegen`) und steht deshalb auch in
          der Reihenfolge, in der man sie sieht — zwischen den Fächern, nicht
          hinter ihnen. Ohne Anlegerecht gibt es keine Mitte: Eine leere
          Spalte wäre ein Loch in der Leiste. */}
      {anlegen ? <Anlegen gruppen={gruppen} eigen={eigen} aktiv={anlegenWeg?.weg === aktiv} /> : null}
      {punkte.slice(schnitt).map(fach)}
    </nav>
  );
}
